import type { ReactNode } from "react";
import { Code2, Globe, Key, Layers, Settings2, Zap } from "lucide-react";
import { StepInfoRow } from "../StepInfoRow";
import { StepCodeBlock } from "../StepCodeBlock";
import { getString, getNumber, isSensitiveHeader, maskHeaderValue } from "./helpers";
import type { SummaryTranslator } from "./types";

export function getApiExtractSummary(config: Record<string, unknown>, t: SummaryTranslator): ReactNode[] {
  const items: ReactNode[] = [];

  const url = getString(config.url);
  const domain = getString(config.domain);
  const protocol = getString(config.protocol) || "https";
  const method = getString(config.method) || "GET";
  const authResourceId = getString(config.authResourceId);
  const authResourceName = getString(config.authResourceName);
  const headers = config.headers as Record<string, string> | undefined;
  const dataPath = getString(config.dataPath);
  const paginationType = getString(config.paginationType);
  const rateLimitRps = getNumber(config.rateLimitRps);
  const maxRows = getNumber(config.maxRows);

  const displayUrl = url?.startsWith("http") ? url : domain ? `${protocol}://${domain}${url || ""}` : url;

  if (displayUrl) {
    items.push(
      <div key="endpoint" className="flex items-start gap-2 text-xs">
        <span className="mt-0.5 shrink-0 text-gray-400"><Globe className="size-3.5" /></span>
        <span className="inline-flex min-w-0 items-baseline gap-0 overflow-hidden rounded border border-purple-200 dark:border-purple-800">
          <span className="shrink-0 bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
            {method}
          </span>
          <span className="break-all px-1.5 py-0.5 font-mono text-[11px] text-gray-700 dark:text-gray-300">
            {displayUrl}
          </span>
        </span>
      </div>
    );
  }

  if (authResourceId) {
    const authDisplay = authResourceName || t("summaries.api_extract.auth_resource_linked");
    items.push(
      <StepInfoRow key="auth" icon={<Key className="size-3.5" />} label={t("summaries.api_extract.label_auth")} value={authDisplay} />
    );
  }

  if (headers && typeof headers === "object") {
    const headerEntries = Object.entries(headers);
    if (headerEntries.length > 0) {
      items.push(
        <div key="headers" className="space-y-0.5">
          <div className="flex items-center gap-2 text-xs">
            <span className="shrink-0 text-gray-400"><Settings2 className="size-3.5" /></span>
            <span className="text-gray-500 dark:text-gray-400">{t("summaries.api_extract.label_headers", { count: headerEntries.length })}</span>
          </div>
          <div className="ml-5.5 space-y-px">
            {headerEntries.map(([key, value]) => (
              <div key={key} className="flex items-center gap-1 font-mono text-[11px]">
                <span className="shrink-0 text-gray-600 dark:text-gray-400">{key}:</span>
                <span className={`truncate ${isSensitiveHeader(key) ? "text-amber-600 dark:text-amber-400" : "text-gray-500"}`}>
                  {isSensitiveHeader(key) ? maskHeaderValue(String(value)) : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
  }

  const body = getString(config.body);
  if (body) {
    let prettyBody = body;
    try {
      prettyBody = JSON.stringify(JSON.parse(body), null, 2);
    } catch {
      // keep raw
    }
    items.push(
      <StepCodeBlock key="body" icon={<Code2 className="size-3.5" />} label={t("summaries.api_extract.label_body")} code={prettyBody} title="Request Body" language="json" />
    );
  }

  if (dataPath) {
    items.push(
      <StepInfoRow key="dataPath" icon={<Layers className="size-3.5" />} label={t("summaries.api_extract.label_data_path")} value={dataPath} mono />
    );
  }

  if (paginationType && paginationType !== "none") {
    const paginationLabels: Record<string, string> = {
      cursor: t("summaries.api_extract.pagination_cursor"),
      page: t("summaries.api_extract.pagination_page"),
      offset: t("summaries.api_extract.pagination_offset"),
      link: t("summaries.api_extract.pagination_link"),
    };
    items.push(
      <StepInfoRow key="pagination" icon={<Zap className="size-3.5" />} label={t("summaries.api_extract.label_pagination")} value={paginationLabels[paginationType] || paginationType} />
    );
  }

  if (rateLimitRps) {
    items.push(
      <StepInfoRow key="rateLimit" icon={<Settings2 className="size-3.5" />} label={t("summaries.api_extract.label_rate_limit")} value={t("summaries.api_extract.rate_limit_rps", { rps: rateLimitRps })} />
    );
  }

  if (maxRows) {
    items.push(
      <StepInfoRow key="maxRows" icon={<Settings2 className="size-3.5" />} label={t("summaries.api_extract.label_max_rows")} value={maxRows.toLocaleString()} />
    );
  }

  return items;
}
